// Doc-Ref: docs/features/collaborations-current.md
import { useState, useEffect } from "react";
import { Filter, RotateCcw } from "lucide-react";

import { Button } from "components/ui/button";
import { Slider } from "components/ui/slider";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "components/ui/toggle-group";
import { MultiSelect } from "components/ui/multi-select";
import SubjectAreaFilter from "./SubjectAreaFilter";
import { cn } from "lib/utils";

const FiltersRow = ({ filters = {}, onFilterChange = () => { }, onReset, entityType = "institution", entityHasPending = false, onRunAnalysis }) => {
  const currentYear = new Date().getFullYear();

  // Use local state to manage filters before applying
  const [localFilters, setLocalFilters] = useState(filters);

  // Sync localFilters if the parent's filters prop changes completely (e.g. initial load or external reset)
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const getCollaboratorTypeOptions = () => {
    if (entityType === "researcher") {
      return [
        { label: "Researcher", value: "researcher" },
        { label: "Institution", value: "institution" },
      ];
    } else if (entityType === "unit") {
      return [{ label: "Institution", value: "institution" }];
    } else if (entityType === "institution") {
      return [{ label: "Institution", value: "institution" }];
    }
    return [{ label: "Institution", value: "institution" }];
  };

  const US_STATES = [
    { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" }, { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" },
    { value: "FL", label: "Florida" }, { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" }, { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" }, { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" }, { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" }, { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" }, { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" }, { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" }, { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" }, { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" }, { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" }, { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" }, { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" }, { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" }, { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" },
    { value: "DC", label: "District of Columbia" },
  ];

  const filterHasChanges = JSON.stringify(localFilters) !== JSON.stringify(filters);
  const hasAnyPending = filterHasChanges || entityHasPending;

  const handleApply = () => {
    if (onRunAnalysis) {
      onRunAnalysis(localFilters);
    } else {
      onFilterChange(localFilters);
    }
  };

  const handleReset = () => {
    const defaultFilters = {
      yearRange: "last_10_years",
      subjectArea: [],
      geography: "us",
      state: [],
      metric: "publications",
      collaboratorType: entityType === "researcher" ? "researcher" : "institution",
      keywordsOperator: "or",
    };
    setLocalFilters(defaultFilters);
    onFilterChange(defaultFilters);
    if (onReset) onReset();
  };

  return (
    <div className="flex flex-col gap-8 p-6">

      {/* Year Range */}
      <div className="flex flex-col gap-3">
        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
          Publication year range
        </label>
        <Select
          value={localFilters.yearRange || "last_10_years"}
          onValueChange={(val) => {
            setLocalFilters({ ...localFilters, yearRange: val });
          }}
        >
          <SelectTrigger className="h-9 w-full bg-white border-gray-200 text-sm hover:bg-gray-50/50 transition-colors">
            <SelectValue placeholder="Select year range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="last_5_years">Last 5 years</SelectItem>
            <SelectItem value="last_10_years">Last 10 years</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Subject Area */}
      <div className="flex flex-col gap-3">
        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
          Subject area
        </label>
        <SubjectAreaFilter
          filters={localFilters}
          setFilters={(newVals) => setLocalFilters((prev) => ({ ...prev, ...newVals }))}
          entity="subjectArea"
        />
        {localFilters.subjectArea && localFilters.subjectArea.length >= 2 && (
          <ToggleGroup
            type="single"
            value={localFilters.keywordsOperator || 'or'}
            onValueChange={(val) => {
              if (val) setLocalFilters({ ...localFilters, keywordsOperator: val });
            }}
            className="mt-1 w-full justify-start border border-gray-100 rounded-md p-1 bg-gray-50"
          >
            <ToggleGroupItem value="or" className="flex-1 h-7 text-[11px] font-medium data-[state=on]:bg-white data-[state=on]:shadow-sm data-[state=on]:text-explore text-gray-500">
              Match Any
            </ToggleGroupItem>
            <ToggleGroupItem value="and" className="flex-1 h-7 text-[11px] font-medium data-[state=on]:bg-white data-[state=on]:shadow-sm data-[state=on]:text-explore text-gray-500">
              Match All
            </ToggleGroupItem>
          </ToggleGroup>
        )}
      </div>

      {/* State */}
      <div className="flex flex-col gap-3">
        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
          State
        </label>
        <MultiSelect
          options={US_STATES}
          value={localFilters.state || []}
          onChange={(val) => setLocalFilters({ ...localFilters, state: val })}
          placeholder="All states"
          searchPlaceholder="Search state..."
        />
      </div>


      {/* Actions */}
      <div className="flex flex-col gap-3 mt-6">
        <Button 
          onClick={handleApply} 
          className={cn(
            "w-full h-10 font-medium flex items-center justify-center gap-2 shadow-sm rounded-md transition-all",
            hasAnyPending
              ? "bg-slate-900 hover:bg-slate-800 text-white"
              : "bg-gray-100 text-gray-400 border border-gray-200 cursor-default"
          )}
          disabled={!hasAnyPending}
        >
          <Filter size={16} className={hasAnyPending ? "text-white/70" : "text-gray-400"} />
          Run Analysis
        </Button>
        <button
          onClick={handleReset}
          className="w-full text-xs text-gray-400 hover:text-gray-700 flex items-center justify-center gap-1.5 transition-colors mt-1"
        >
          <RotateCcw size={12} />
          Reset to default
        </button>
      </div>

    </div>
  );
};

export default FiltersRow;
